import { defineComponent as _defineComponent } from 'vue'
import router from '@/router';
import { BILLING_PLAN_DETAILS } from '@/routerNames';
import billingService from '@/services/BillingService';
import { computed, onMounted, ref } from 'vue';
import BackSnaploreButton from '@/components/pages/billing/PlansPage/BackSnaploreButton.vue';
import store from '@/store';
import { Price, Subscription } from '@/@types/billing';
import PlansEnterpriseFooter from '@/components/pages/billing/PlansPage/PlansEnterpriseFooter.vue';
import PricingTabs from '@/components/pages/billing/PlansPage/PricingTabs/PricingTabs.vue';
import apiService from '@/services/ApiService';
import { Loading } from 'element-ui';
import { useRoute } from 'vue-router/composables';
import moment from 'moment';
import { GUIDES_APP, isGuidesContext, isGuidesOnlyCompany } from '@/helpers/appContext';
import { BILLING_ERROR_MESSAGES, billingErrorMessage, isPaymentPendingError } from '@/helpers/billingErrors';
import {
  findCompanySubscription,
  hasOtherProductSubscription as computeHasOtherProductSubscription,
  resolvePlansPageIsGuides,
} from '@/helpers/planProduct';
import notificationService from '@/services/NotificationService';


export default /*#__PURE__*/_defineComponent({
  __name: 'PlansPage',
  setup(__props) {

const route = useRoute();
const { params } = route;

const prices = ref<Price[]>([]);
const subscription = ref<Subscription>();

const requiresUpdate = computed(() => !!params.update);
const company = computed(() => store.state.company);
const isCompanyPaid = computed(() => store.state.company.isPaid);
const isUpdatePlan = computed(() => requiresUpdate.value && isCompanyPaid.value);

// C7: a change-plan visit must derive the product from the company's ACTUAL
// current subscription tag, not the session/query context — a fresh tab
// (empty sessionStorage) opening Settings → Billing → Change Plan otherwise
// showed Snaplore prices for a Guides-only company. Guides signups reach this
// page via the paid-company middleware (query params are dropped there) — the
// context is carried by sessionStorage since the auth pages for the initial
// subscribe flow, with the route query as an explicit override.
const isGuides = computed(() =>
  resolvePlansPageIsGuides({
    isUpdatePlan: isUpdatePlan.value,
    companyPaddleApp: company.value?.paddle?.app,
    queryIsGuides: isGuidesContext(route.query),
    companyIsGuidesOnly: isGuidesOnlyCompany(company.value),
  })
);

// D2: a company already paying (active/trialing/past_due) for the OTHER
// product must see a non-checkout notice instead of subscribe buttons.
const hasOtherProductSubscription = computed(() =>
  computeHasOtherProductSubscription({
    isUpdatePlan: isUpdatePlan.value,
    paddleStatus: company.value?.paddle?.status,
    paddleApp: company.value?.paddle?.app,
    isGuides: isGuides.value,
  })
);

// The backend answered PAYMENT_PENDING: a checkout was paid and only its webhook is missing.
const paymentPending = ref(false);
const paymentPendingMessage = BILLING_ERROR_MESSAGES.PAYMENT_PENDING;

const handlePlanUpdate = async (priceId: Price['id']) => {
  // Check if there's a scheduled change within 35 minutes
  if (subscription.value?.currentBillingPeriod?.endsAt) {
    const nextBilledAt = moment.utc(subscription.value?.currentBillingPeriod?.endsAt);
    const now = moment.utc();
    const minutesUntilChange = nextBilledAt.diff(now, 'minutes');

    if (minutesUntilChange <= 35 && minutesUntilChange > 0) {
      apiService.handleResponseError({
        response: {
          status: 400,
          data: {
            error:
              'Plan changes are temporarily unavailable. Your subscription update is being processed and will be effective soon.',
          },
        },
      });
      return;
    }
  }

  const loader = Loading.service({ lock: true });

  const subscriptionId = company.value.paddle.subscriptionId;

  try {
    await apiService.billing.changePlan({
      subscriptionId,
      priceId,
    });
    await router.push({ name: BILLING_PLAN_DETAILS });
  } catch (e) {
    const readableMessage = billingErrorMessage(e);
    if (readableMessage) {
      notificationService.error(readableMessage);
    } else {
      apiService.handleResponseError(e);
    }
  }

  loader.close();
  loader.$destroy();
};

const handleSubscribe = async (priceId: Price['id']) => {
  try {
    const {
      data: { transactionId },
    } = await apiService.billing.createTransaction({ priceId, ...(isGuides.value ? { product: GUIDES_APP } : {}) });
    billingService.redirectToCheckout(transactionId, isGuides.value ? { app: GUIDES_APP } : undefined);
  } catch (error) {
    if (isPaymentPendingError(error)) paymentPending.value = true;
    const readableMessage = billingErrorMessage(error);
    if (readableMessage) {
      notificationService.error(readableMessage);
    } else {
      apiService.handleResponseError(error);
    }
  }
};

onMounted(async () => {
  const loader = Loading.service({ lock: true });

  // Only a plan change needs the current subscription, and elsewhere its interval would hide prices. A non-billing
  // company counts as paid with no subscription, and its owner usually has no Paddle customer, so this would answer
  // 404. Failing or not, it must not keep the prices from loading.
  try {
    if (isUpdatePlan.value && company.value?.paddle?.subscriptionId) {
      await apiService.billing.getUserSubscriptions().then(({ data }) => {
        subscription.value = findCompanySubscription(data, company.value);
      });
    }
  } catch (e) {
    apiService.handleResponseError(e);
  }

  try {
    await Promise.all([
      apiService.billing.getPrices(isGuides.value ? GUIDES_APP : undefined).then(({ data }) => {
        prices.value = data.sort((a: any, b: any) => {
          // Prioritize objects with a trialPeriod
          if (a.trialPeriod && !b.trialPeriod) return -1;
          if (!a.trialPeriod && b.trialPeriod) return 1;
          return a - b;
        });
      }),
    ]);
  } catch (e) {
    apiService.handleResponseError(e);
  }

  loader.close();
  loader.$destroy();
});

return { __sfc: true,route, params, prices, subscription, requiresUpdate, company, isCompanyPaid, isUpdatePlan, isGuides, hasOtherProductSubscription, paymentPending, paymentPendingMessage, handlePlanUpdate, handleSubscribe, BackSnaploreButton, PlansEnterpriseFooter, PricingTabs }
}

})